import React, { useState, useEffect, useMemo } from "react";
import { getFirestore, collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import { Table, TableBody, TableContainer, TableHead, Paper, TextField, Box, Tooltip, CircularProgress, Typography } from "@mui/material";
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import moment from "moment";
import "moment/locale/es";

const firestore = getFirestore();

const ProdxUser = () => {
    const [mesSeleccionado, setMesSeleccionado] = useState(moment().format("YYYY-MM"));
    const [ordenesPorUsuario, setOrdenesPorUsuario] = useState(null);
    const [feriados, setFeriados] = useState({});

    useEffect(() => {
        const cargarOrdenes = async () => {
            setOrdenesPorUsuario(null);
            try {
                const [year, month] = mesSeleccionado.split("-");
                const inicioMes = `${year}${month}01`;
                const finMes = `${year}${month}31`;

                // Cargar feriados del mes
                const diasDocRef = doc(firestore, `asistencias/${year}/${month}/dias`);
                const diasDocSnap = await getDoc(diasDocRef);
                if (diasDocSnap.exists()) {
                    setFeriados(diasDocSnap.data().feriados || {});
                } else {
                    setFeriados({});
                }

                const ordenesRef = collection(firestore, "ordenes");
                
                // Consulta eficiente usando confectionDate (formato YYYYMMDD)
                const q = query(
                    ordenesRef, 
                    where("confectionDate", ">=", inicioMes), 
                    where("confectionDate", "<=", finMes)
                );
                
                const snapshot = await getDocs(q);
                let data = snapshot.docs.map(doc => doc.data());

                // Agrupar órdenes por usuario y día
                let agrupadas = {};
                data.forEach(orden => {
                    const { user, confectionDate, confectionData } = orden;

                    let dia;
                    
                    // Priorizar confectionDate (nuevo formato) sobre confectionData (formato legacy)
                    if (confectionDate) {
                        // Formato YYYYMMDD - extraer día
                        dia = parseInt(confectionDate.slice(-2));
                    } else if (confectionData) {
                        // Formato legacy "DD/MM/YYYY HH:MM" - para compatibilidad con datos existentes
                        const fechaConfection = moment(confectionData, "DD/MM/YYYY HH:mm");
                        if (!fechaConfection.isValid()) return;
                        dia = fechaConfection.date();
                    } else {
                        return; // Sin fecha de confección
                    }

                    if (!agrupadas[user]) {
                        agrupadas[user] = { user, total: 0, dias: {} };
                    }
                    if (!agrupadas[user].dias[dia]) {
                        agrupadas[user].dias[dia] = 0;
                    }

                    agrupadas[user].dias[dia]++;
                    agrupadas[user].total++;
                });

                setOrdenesPorUsuario(agrupadas);
            } catch (err) {
                console.error("Error loading user productivity data:", err);
                setOrdenesPorUsuario({});
            }
        };

        cargarOrdenes();
    }, [mesSeleccionado]);

    const diasMes = moment(mesSeleccionado, "YYYY-MM").daysInMonth();

    return (
        <div>
            <h2>Productividad de los Usuarios</h2>
            <Box display="flex" justifyContent="center" marginBottom={2}>
                <TextField
                    type="month"
                    value={mesSeleccionado}
                    onChange={(e) => setMesSeleccionado(e.target.value)}
                />
            </Box>
            <TableContainer
                    component={Paper}
                sx={{
                    maxHeight: '75vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                        padding: '2px 4px',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none'
                    }
                }}
            >
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <StyledTableCell
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1001,
                                    minWidth: '180px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Usuario
                            </StyledTableCell>
                            {[...Array(diasMes).keys()].map(i => {
                                const dayNum = i + 1;
                                const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum).locale('es');
                                const diaSemana = fecha.format('dd');
                                const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                                const esDomingo = fecha.day() === 0;
                                const esFinde = esDomingo || fecha.day() === 6;
                                const esFeriado = !!feriados[dayNum];
                                const headerSx = esFeriado
                                    ? { backgroundColor: '#c9b501 !important', color: '#827717' }
                                    : esDomingo
                                        ? { backgroundColor: '#f6882d !important', color: '#e65100' }
                                        : {};
                                return (
                                    <Tooltip key={i} title={esFeriado ? `Feriado: ${feriados[dayNum]}` : ''} arrow disableHoverListener={!esFeriado}>
                                        <StyledTableCell
                                            align="center"
                                            sx={{
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                lineHeight: 1,
                                                ...headerSx,
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                                <span style={{ fontSize: '0.6rem', opacity: 0.75, fontWeight: esFinde || esFeriado ? 700 : 400 }}>
                                                    {diaCapitalizado}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {dayNum}
                                                </span>
                                            </div>
                                        </StyledTableCell>
                                    </Tooltip>
                                );
                            })}
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Total</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Prom. diario</StyledTableCell>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        {Object.keys(ordenesPorUsuario || {}).length > 0 ? (
                            Object.keys(ordenesPorUsuario).map((user) => {
                                const total = ordenesPorUsuario[user].total;
                                const dias = ordenesPorUsuario[user].dias;
                                const diasConProduccion = [...Array(diasMes).keys()].filter(i => dias[i + 1]).length;
                                const promedioDiario = diasConProduccion > 0 ? (total / diasConProduccion).toFixed(2) : "-";
                                return (
                                    <StyledTableRow key={user} sx={{ height: '32px' }}>
                                        <StyledTableCell
                                            sx={{
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 1000,
                                                minWidth: '180px',
                                                backgroundColor: '#ffffff',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            {ordenesPorUsuario[user].user}
                                        </StyledTableCell>
                                        {[...Array(diasMes).keys()].map(i => {
                                            const dayNum = i + 1;
                                            const count = ordenesPorUsuario[user].dias[dayNum] || 0;
                                            const hasOrders = count > 0;

                                            const fecha = moment(mesSeleccionado, "YYYY-MM").date(dayNum);
                                            const esDomingo = fecha.day() === 0;
                                            const esFeriado = !!feriados[dayNum];

                                            let cellBg = 'inherit';
                                            if (esDomingo || esFeriado) {
                                                cellBg = hasOrders ? "#c8e6c9" : esDomingo ? "#ffcc80" : "#fff176";
                                            } else {
                                                cellBg = hasOrders ? 'inherit' : '#e8f5e9';
                                            }

                                            return (
                                                <StyledTableCell
                                                    key={dayNum}
                                                    align="center"
                                                    sx={{
                                                        backgroundColor: cellBg,
                                                        minWidth: '36px',
                                                        width: '36px',
                                                        padding: '2px 4px',
                                                        fontSize: '0.8rem',
                                                    }}
                                                >
                                                    {count || "-"}
                                                </StyledTableCell>
                                            );
                                        })}
                                        <StyledTableCell align="center">{total}</StyledTableCell>
                                        <StyledTableCell align="center">{promedioDiario}</StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={35} align="center">
                                    {ordenesPorUsuario !== null && Object.keys(ordenesPorUsuario).length === 0 ? (
                                        <Typography variant="h5">No se encontraron resultados</Typography>
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default ProdxUser;
